import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal from "../../ui/layout/Modal";

const NavButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.info.light}`,
  borderRadius: theme.shape.borderRadius,
  width: 48,
  height: 48,
  color: theme.palette.primary.main,
  flexShrink: 0,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  "&.Mui-disabled": {
    opacity: 0.35,
    borderColor: theme.palette.divider,
  },
}));

const PreviewImage = styled("img")({
  maxWidth: "100%",
  maxHeight: 500,
  height: "auto",
  margin: "0 auto",
  display: "block",
  objectFit: "contain",
});

const AttachmentModal = ({
  show,
  handleClose,
  attachments = [],
  defaultSelected = 0,
}) => {
  const [selectedImage, setSelectedImage] = useState(defaultSelected);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setSelectedImage(defaultSelected);
    setIsLoading(true);
  }, [defaultSelected]);

  const handleSlideChange = (dir) => {
    setIsLoading(true);
    setSelectedImage((prev) =>
      dir === "prev"
        ? prev === 0 ? attachments.length - 1 : prev - 1
        : prev === attachments.length - 1 ? 0 : prev + 1
    );
  };

  const canNavigate = attachments.length > 1;

  return (
    <Modal
      open={show}
      width="xl"
      handleClose={handleClose}
      modalTitle="Attachments"
      action={() => (
        <Button variant="outlined" color="secondary" onClick={handleClose}>
          Close
        </Button>
      )}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
        {/* Prev */}
        <NavButton
          disabled={!canNavigate}
          onClick={() => handleSlideChange("prev")}
          aria-label="Previous attachment"
        >
          <KeyboardArrowLeft />
        </NavButton>

        <Box sx={{ flex: 1, textAlign: "center", minHeight: 200, position: "relative" }}>
          {attachments.map((att, index) => (
            <Box
              key={att.id}
              sx={{ display: selectedImage === index ? "block" : "none" }}
            >
              {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              <PreviewImage
                src={att.url}
                alt={att.name}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
                sx={{ display: isLoading ? "none" : "block" }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mt: 2.5, textAlign: "center" }}
              >
                {att.file_name}
              </Typography>
            </Box>
          ))}

          {canNavigate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {selectedImage + 1} / {attachments.length}
            </Typography>
          )}
        </Box>

        {/* Next */}
        <NavButton
          disabled={!canNavigate}
          onClick={() => handleSlideChange("next")}
          aria-label="Next attachment"
        >
          <KeyboardArrowRight />
        </NavButton>
      </Stack>
    </Modal>
  );
};

export default AttachmentModal;